import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GOALS } from "@/lib/campaigns";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Minus, Plus, Sparkles, Loader2 } from "lucide-react";

const STEPS = ["Goal", "Duration", "Content mix", "Review"];
const DEFAULT_MIX = { reels: 3, carousels: 2, hooks: 10, blog: false, email: false };
const CLAMP = { reels: [1, 5], carousels: [1, 3], hooks: [5, 20] };

export default function NewCampaign() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ can_use: true, flat_cost: 20, is_unlimited: false });
  const [form, setForm] = useState({ goal_key: "", name: "", goal_notes: "", duration_days: 14, start_date: new Date().toISOString().slice(0, 10), mix: DEFAULT_MIX });

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/campaigns"); setMeta({ can_use: data.can_use, flat_cost: data.flat_cost, is_unlimited: data.is_unlimited }); }
    catch (err) { console.debug("Campaign gate probe failed", err); }
  })(); }, []);

  useEffect(() => { if (!meta.can_use) nav("/campaigns"); }, [meta.can_use, nav]);

  const canNext = () => {
    if (step === 0) return form.goal_key && form.name.trim().length >= 3;
    if (step === 1) return [7, 14, 30].includes(form.duration_days) && !!form.start_date;
    if (step === 2) return (form.mix.reels + form.mix.carousels) > 0 || form.mix.hooks > 0 || form.mix.blog || form.mix.email;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", form);
      const cid = data.campaign.id;
      // Kick off background generation and land on the detail page which polls.
      api.post(`/campaigns/${cid}/generate`).catch(() => {});
      toast.success("Generation started");
      nav(`/campaigns/${cid}`);
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Could not create campaign"));
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 lg:px-10 py-8 lg:py-12" data-testid="new-campaign-page">
      <button onClick={() => nav("/campaigns")} className="inline-flex items-center gap-1.5 text-[13px] text-[#64748B] hover:text-[#0A2342] mb-6"><ArrowLeft className="w-3.5 h-3.5" /> Campaigns</button>

      {/* Editorial ribbon — responsive: labels collapse to numbers-only on mobile */}
      <div className="flex items-center gap-2 sm:gap-3 mb-10 overflow-hidden">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center flex-1 min-w-0">
            <div className={`flex items-center gap-2 sm:gap-3 min-w-0 ${i === step ? "" : i < step ? "opacity-100" : "opacity-40"}`}>
              <div className={`font-heading font-black text-[12px] sm:text-[13px] px-2 sm:px-2.5 py-1.5 rounded-md border tracking-tighter shrink-0 ${i === step ? "bg-[#0A2342] text-white border-[#0A2342]" : i < step ? "bg-white text-[#0A2342] border-[#0A2342]" : "bg-white text-[#94A3B8] border-[#E2E8F0]"}`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className={`hidden md:block text-[11px] lg:text-[12px] tracking-[0.16em] lg:tracking-[0.18em] uppercase font-bold truncate ${i === step ? "text-[#0A2342]" : "text-[#94A3B8]"}`}>{label}</div>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-[2px] bg-[#E2E8F0] mx-2 sm:mx-3 min-w-[8px]" />}
          </div>
        ))}
      </div>

      {step === 0 && <StepGoal form={form} setForm={setForm} />}
      {step === 1 && <StepDuration form={form} setForm={setForm} />}
      {step === 2 && <StepMix form={form} setForm={setForm} />}
      {step === 3 && <StepReview form={form} meta={meta} />}

      <div className="flex items-center justify-between mt-10">
        <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="text-[13.5px] font-semibold text-[#64748B] px-3 py-2 hover:text-[#0A2342] disabled:opacity-40 disabled:cursor-not-allowed">Back</button>
        {step < STEPS.length - 1 ? (
          <button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={!canNext()} data-testid="wizard-next-btn" className="dms-btn-primary inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed">
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={submit} disabled={busy} data-testid="wizard-generate-btn" className="dms-btn-primary inline-flex items-center gap-2 px-6 py-3">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate campaign
          </button>
        )}
      </div>
    </div>
  );
}

function StepGoal({ form, setForm }) {
  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-[#0A2342] mb-2">What&apos;s the goal?</h2>
      <p className="text-[13.5px] text-[#64748B] mb-6">Pick a campaign type. This shapes the arc, tone and structure of every piece.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {GOALS.map((g) => {
          const Icon = g.icon;
          const selected = form.goal_key === g.key;
          return (
            <button
              key={g.key}
              onClick={() => setForm((f) => ({ ...f, goal_key: g.key }))}
              data-testid={`wizard-goal-${g.key}`}
              className={`text-left p-5 rounded-xl bg-white transition-all hover:-translate-y-0.5 hover:shadow-lg relative overflow-hidden ${g.dashed ? "border-2 border-dashed" : "border"} ${selected ? "border-[#4FD1C5] ring-2 ring-[#4FD1C5]/30 shadow-md" : "border-[#E2E8F0] hover:border-[#4FD1C5]/60"}`}
            >
              <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full opacity-10" style={{ backgroundColor: g.color }} />
              <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${g.color}22`, color: g.color }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-heading font-bold text-[16px] text-[#0A2342] leading-tight">{g.name}</div>
              <div className="text-[12.5px] text-[#64748B] mt-1.5 leading-relaxed">{g.pitch}</div>
            </button>
          );
        })}
      </div>
      <div className="mt-8 space-y-3">
        <div>
          <label className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Campaign name</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} maxLength={80} placeholder="e.g. Reef Awareness Week — July" data-testid="wizard-campaign-name" className="mt-1.5 w-full text-[15px] px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#4FD1C5]" />
        </div>
        {form.goal_key === "custom" && (
          <div>
            <label className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Describe your goal</label>
            <textarea value={form.goal_notes} onChange={(e) => setForm((f) => ({ ...f, goal_notes: e.target.value }))} maxLength={600} rows={4} placeholder="Tell us the specific angle, offer, and audience for this campaign." className="mt-1.5 w-full text-[14px] px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#4FD1C5]" />
          </div>
        )}
      </div>
    </div>
  );
}

function StepDuration({ form, setForm }) {
  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-[#0A2342] mb-2">How long is the push?</h2>
      <p className="text-[13.5px] text-[#64748B] mb-6">Pick a duration. Pieces are spread evenly across the window with the last drop before day-end.</p>
      <div className="grid grid-cols-3 gap-4">
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            onClick={() => setForm((f) => ({ ...f, duration_days: d }))}
            data-testid={`wizard-duration-${d}`}
            className={`p-6 rounded-xl bg-white transition-all border-2 ${form.duration_days === d ? "border-[#4FD1C5] shadow-md ring-2 ring-[#4FD1C5]/20" : "border-[#E2E8F0] hover:border-[#4FD1C5]/60"}`}
          >
            <div className="font-heading font-black text-4xl text-[#0A2342] tracking-tight">{d}</div>
            <div className="text-[11.5px] tracking-[0.22em] uppercase font-bold text-[#64748B] mt-1">Days</div>
          </button>
        ))}
      </div>
      <div className="mt-8">
        <label className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Start date</label>
        <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="wizard-start-date" className="mt-1.5 w-full max-w-xs text-[15px] px-4 py-3 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#4FD1C5]" />
      </div>
    </div>
  );
}

function Stepper({ label, keyName, value, onChange, min, max, testid }) {
  return (
    <div className="flex items-center justify-between p-5 bg-white border border-[#E2E8F0] rounded-xl">
      <div>
        <div className="font-heading font-bold text-[16px] text-[#0A2342]">{label}</div>
        <div className="text-[12px] text-[#64748B] mt-0.5">{min === max ? "" : `${min}-${max}`}</div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onChange(Math.max(min, value - 1))} className="w-10 h-10 rounded-md border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F1F5F9] disabled:opacity-40" disabled={value <= min}><Minus className="w-4 h-4" /></button>
        <div className="w-14 text-center font-heading font-black text-2xl text-[#0A2342]" data-testid={testid}>{value}</div>
        <button onClick={() => onChange(Math.min(max, value + 1))} className="w-10 h-10 rounded-md border border-[#E2E8F0] flex items-center justify-center hover:bg-[#F1F5F9] disabled:opacity-40" disabled={value >= max}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-5 bg-white border border-[#E2E8F0] rounded-xl">
      <div className="font-heading font-bold text-[16px] text-[#0A2342]">{label}</div>
      <button onClick={() => onChange(!checked)} data-testid={testid} className={`relative w-12 h-7 rounded-full transition-colors ${checked ? "bg-[#4FD1C5]" : "bg-[#E2E8F0]"}`}>
        <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${checked ? "left-[calc(100%-1.625rem)]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function StepMix({ form, setForm }) {
  const setMix = (patch) => setForm((f) => ({ ...f, mix: { ...f.mix, ...patch } }));
  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-[#0A2342] mb-2">What should we produce?</h2>
      <p className="text-[13.5px] text-[#64748B] mb-6">All pieces share one narrative arc, so bumping counts up won&apos;t dilute the story.</p>
      <div className="space-y-3">
        <Stepper label="Reels" keyName="reels" value={form.mix.reels} onChange={(v) => setMix({ reels: v })} min={CLAMP.reels[0]} max={CLAMP.reels[1]} testid="wizard-mix-reels-count" />
        <Stepper label="Carousels" keyName="carousels" value={form.mix.carousels} onChange={(v) => setMix({ carousels: v })} min={CLAMP.carousels[0]} max={CLAMP.carousels[1]} testid="wizard-mix-carousels-count" />
        <Stepper label="Hook bank" keyName="hooks" value={form.mix.hooks} onChange={(v) => setMix({ hooks: v })} min={CLAMP.hooks[0]} max={CLAMP.hooks[1]} testid="wizard-mix-hooks-count" />
        <Toggle label="Pillar blog post" checked={form.mix.blog} onChange={(v) => setMix({ blog: v })} testid="wizard-mix-blog-toggle" />
        <Toggle label="Marketing email" checked={form.mix.email} onChange={(v) => setMix({ email: v })} testid="wizard-mix-email-toggle" />
      </div>
    </div>
  );
}

function StepReview({ form, meta }) {
  const goal = GOALS.find((g) => g.key === form.goal_key);
  const totalPieces = form.mix.reels + form.mix.carousels + (form.mix.hooks ? 1 : 0) + (form.mix.blog ? 1 : 0) + (form.mix.email ? 1 : 0);
  return (
    <div>
      <h2 className="font-heading font-bold text-2xl text-[#0A2342] mb-2">Ready to fire?</h2>
      <p className="text-[13.5px] text-[#64748B] mb-6">One click and we&apos;ll design the arc, then generate every piece into your vault.</p>
      <div className="dms-card p-8 space-y-5">
        <Row label="Campaign" value={form.name} />
        <Row label="Goal" value={goal?.name || form.goal_key} />
        <Row label="Duration" value={`${form.duration_days} days · starts ${form.start_date}`} />
        <Row label="Mix" value={`${form.mix.reels} reel(s), ${form.mix.carousels} carousel(s), ${form.mix.hooks} hook(s)${form.mix.blog ? ", 1 blog" : ""}${form.mix.email ? ", 1 email" : ""}`} />
        <Row label="Total pieces" value={`${totalPieces} pieces + 1 narrative arc`} />
        <div className="pt-4 border-t border-[#F1F5F9]">
          <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#64748B]">Cost</div>
          <div className="font-heading font-black text-4xl text-[#0A2342] mt-1 tracking-tight" data-testid="wizard-cost-preview">
            {meta.is_unlimited ? "Free" : `${meta.flat_cost} credits`}
          </div>
          <div className="text-[12px] text-[#64748B] mt-1">
            {meta.is_unlimited ? "Pro Unlimited — campaigns don't cost credits." : "Flat rate — regardless of how many pieces you generated."}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#64748B] flex-shrink-0">{label}</div>
      <div className="text-[13.5px] text-[#0A2342] font-semibold text-right">{value}</div>
    </div>
  );
}
